if (!customElements.get('filmstrip-gallery')) {
  class FilmstripGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.filmstrip__viewport');
      this.track = this.querySelector('.filmstrip__track');
      this.items = Array.from(this.querySelectorAll('.filmstrip__item'));
      this.prevBtn = this.querySelector('.filmstrip__arrow--prev');
      this.nextBtn = this.querySelector('.filmstrip__arrow--next');
      this.mode = this.dataset.mode || 'same-height';

      console.log('[Filmstrip Init]', this.dataset.section, 'mode:', this.mode, 'items:', this.items.length, 'hasViewport:', !!this.viewport, 'hasTrack:', !!this.track);

      if (!this.viewport || !this.track || this.items.length === 0) {
        console.log('[Filmstrip Init Failed]', this.dataset.section, 'viewport:', !!this.viewport, 'track:', !!this.track, 'items:', this.items.length);
        return;
      }

      this.currentIndex = 0;
      this.isScrolling = false;
      this.itemPositions = [];
      this.maxScroll = 0;
      this.isInitialized = false;

      // Wait for images to load before calculating positions
      this.waitForImages().then(() => {
        this.calculatePositions();
        this.updateButtons();
        this.isInitialized = true;

        // Recalculate after a brief delay to ensure CSS has fully applied
        // (needed when using 100vw width with negative margins)
        setTimeout(() => {
          this.calculatePositions();
          this.updateButtons();
        }, 100);
      });

      // Recalculate on window resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (this.isInitialized) {
            this.calculatePositions();
            this.updateButtons();
          }
        }, 150);
      });

      // Update buttons on scroll
      this.viewport.addEventListener('scroll', () => {
        if (!this.isScrolling && this.isInitialized) {
          this.updateCurrentIndex();
          this.updateButtons();
        }
      }, { passive: true });

      // Arrow click handlers
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          if (this.isInitialized) this.navigatePrev();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          if (this.isInitialized) this.navigateNext();
        });
      }
    }

    async waitForImages() {
      const images = Array.from(this.querySelectorAll('.filmstrip__img'));

      if (images.length === 0) {
        return Promise.resolve();
      }

      const promises = images.map((img, index) => {
        if (img.complete && img.naturalHeight !== 0) {
          return Promise.resolve();
        }

        return new Promise(resolve => {
          img.addEventListener('load', () => {
            resolve();
          }, { once: true });
          img.addEventListener('error', () => {
            resolve();
          }, { once: true });

          // Fallback timeout in case events don't fire
          setTimeout(() => {
            resolve();
          }, 5000);
        });
      });

      await Promise.all(promises);
    }

    calculatePositions() {
      // Force a reflow to ensure we get current layout values
      void this.viewport.offsetHeight;

      const viewportWidth = this.viewport.clientWidth;
      const scrollWidth = this.viewport.scrollWidth;

      console.log('[Filmstrip]', this.dataset.section, 'mode:', this.mode, 'viewportWidth:', viewportWidth, 'scrollWidth:', scrollWidth, 'canScroll:', scrollWidth > viewportWidth);

      // Store the left position of each item relative to the track
      this.itemPositions = this.items.map((item, index) => {
        const pos = item.offsetLeft;
        return pos;
      });

      // For the last item, calculate position to align right edge
      if (this.items.length > 0) {
        const lastItem = this.items[this.items.length - 1];
        const lastItemWidth = lastItem.offsetWidth;
        const lastItemRightAlignPosition = Math.max(0, scrollWidth - viewportWidth);

        // Replace last position with right-aligned position
        this.itemPositions[this.itemPositions.length - 1] = lastItemRightAlignPosition;
      }

      this.maxScroll = Math.max(0, scrollWidth - viewportWidth);
    }

    updateCurrentIndex() {
      if (!this.itemPositions || this.itemPositions.length === 0) return;

      const scrollLeft = this.viewport.scrollLeft;

      // Find which image we're closest to
      let closestIndex = 0;
      let closestDistance = Math.abs(this.itemPositions[0] - scrollLeft);

      for (let i = 1; i < this.itemPositions.length; i++) {
        const distance = Math.abs(this.itemPositions[i] - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }

      this.currentIndex = closestIndex;
    }

    updateButtons() {
      if (!this.prevBtn || !this.nextBtn) return;

      const scrollLeft = Math.round(this.viewport.scrollLeft);
      const maxScroll = Math.round(this.maxScroll);

      // Disable prev button at start
      this.prevBtn.disabled = scrollLeft <= 1;

      // Disable next button at end
      this.nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }

    navigatePrev() {
      if (this.currentIndex > 0) {
        this.scrollToIndex(this.currentIndex - 1);
      }
    }

    navigateNext() {
      if (this.currentIndex < this.items.length - 1) {
        this.scrollToIndex(this.currentIndex + 1);
      }
    }

    scrollToIndex(index) {
      if (!this.itemPositions || index < 0 || index >= this.itemPositions.length) return;

      this.currentIndex = index;
      this.isScrolling = true;

      const targetPosition = this.itemPositions[index];

      this.viewport.scrollTo({
        left: targetPosition,
        behavior: 'smooth'
      });

      // Reset scrolling flag after animation completes
      setTimeout(() => {
        this.isScrolling = false;
        this.updateButtons();
      }, 500);
    }
  }

  customElements.define('filmstrip-gallery', FilmstripGallery);
}
